import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, duration };

    setToasts((prev) => {
      const updated = [...prev, newToast];
      // Keep only the last 3 toasts
      return updated.slice(-3);
    });
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast, toasts }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return {
    showToast: context.showToast,
  };
}

function ToastContainer({ toasts, onDismiss }) {
  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-3 pointer-events-none"
      role="status"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function Toast({ toast, onDismiss }) {
  const [isExiting, setIsExiting] = useState(false);
  const exitTimerRef = useRef(null);

  // Auto-dismiss timer — properly cleaned up when component unmounts
  useEffect(() => {
    if (!toast.duration) return;
    const timer = setTimeout(() => {
      handleDismiss();
    }, toast.duration);
    return () => clearTimeout(timer);
    // handleDismiss is stable (defined with ref pattern below), safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast.id, toast.duration]);

  // Clean up exit animation timer on unmount
  useEffect(() => {
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, []);

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle2 size={20} className="text-green-400 flex-shrink-0" />;
      case 'error':
        return <XCircle size={20} className="text-red-400 flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle size={20} className="text-amber-400 flex-shrink-0" />;
      case 'info':
      default:
        return <Info size={20} className="text-blue-400 flex-shrink-0" />;
    }
  };

  const getBorderColor = () => {
    switch (toast.type) {
      case 'success':
        return 'border-l-4 border-l-green-500';
      case 'error':
        return 'border-l-4 border-l-red-500';
      case 'warning':
        return 'border-l-4 border-l-amber-500';
      case 'info':
      default:
        return 'border-l-4 border-l-blue-500';
    }
  };

  const handleDismiss = () => {
    setIsExiting(true);
    // Exit animation cleanup — tracked in ref so it can be cancelled on unmount
    exitTimerRef.current = setTimeout(() => {
      onDismiss(toast.id);
    }, 300);
  };

  return (
    <div
      className={`
        flex items-start gap-3 px-4 py-3 rounded-lg
        bg-slate-800 border-slate-700 text-white
        pointer-events-auto shadow-lg
        ${getBorderColor()}
        transition-all duration-300 ease-out
        ${isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0 animate-slide-in'}
      `}
      style={{
        animation: isExiting
          ? 'slideOut 300ms ease-out forwards'
          : 'slideIn 300ms ease-out forwards',
      }}
    >
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideOut {
          from {
            opacity: 1;
            transform: translateX(0);
          }
          to {
            opacity: 0;
            transform: translateX(100%);
          }
        }
      `}</style>

      <div className="mt-0.5">{getIcon()}</div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white break-words">{toast.message}</p>
      </div>

      <button
        onClick={handleDismiss}
        className="flex-shrink-0 text-slate-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-800 focus:ring-blue-500 rounded p-0.5"
        aria-label="Close notification"
      >
        <X size={18} />
      </button>
    </div>
  );
}
